// web/src/utils/buildWebTimelineContext.js
//
// Adapter: maps `WanderAppComplete`'s component state + refs → the
// `context` object shape that shared `createDayTimeline` expects
// (`packages/shared/utils/createDayTimeline.js` line 52–89).
//
// Web's local `createDayTimeline` closure-captured every piece of
// render-scope state; shared's is a pure function that needs the same
// fields passed explicitly. This adapter is a thin, explicit mapping so
// the two copies produce byte-identical timelines and the local copy
// can be deleted in a follow-up.
//
// All fields are optional — shared tolerates missing or empty values
// (see its defensive destructure at the top of the function). Pass what
// you have.

/**
 * @param {Object} params
 * @param {Object} params.itinerary
 * @param {Object} params.selectedDestination
 * @param {Object} params.userProfile — from useAuth (expects .preferences)
 * @param {Object} params.profile — Supabase row (for profile.home_airports)
 * @param {Object} params.arrivalFlight
 * @param {Object} params.driveDetails
 * @param {string} params.transportationMode
 * @param {Object} params.transitTimes
 * @param {Object} params.transportModes
 * @param {Object} params.dynamicTransportModes
 * @param {Array}  params.destinationAirports
 * @param {string} params.effectiveReturnMode
 * @param {Object} params.departureFlight
 * @param {Object} params.quickTransportResult
 * @param {Object} params.airportDistances
 * @param {number|null} params.calculatedDriveTime
 * @param {string} params.customTransportDesc
 * @param {{ current: Object }} params.transitTimesRef
 * @param {{ current: Object }} params.validatedFlightDurations
 * @param {Object} params.selectedHotel
 * @param {string} params.accommodationType
 * @param {string} params.generatedHotelName
 * @param {string} params.customAccommodationName
 * @param {string} params.tripType
 * @param {Array}  params.tripBuilderStops
 * @param {boolean} params.hasOvernightOutbound
 * @param {Object} params.dynamicGeographyCache — module-scope mutable cache
 * @param {Object} [params.perLegTransport] — legTransports state keyed by leg key
 * @param {Object} [params.logger]
 * @returns {Object} context for createDayTimeline
 */
export function buildWebTimelineContext({
  itinerary,
  selectedDestination,
  userProfile,
  profile,
  arrivalFlight,
  driveDetails,
  transportationMode,
  transitTimes,
  transportModes,
  dynamicTransportModes,
  destinationAirports,
  effectiveReturnMode,
  departureFlight,
  quickTransportResult,
  airportDistances,
  calculatedDriveTime,
  customTransportDesc,
  transitTimesRef,
  validatedFlightDurations,
  selectedHotel,
  accommodationType,
  generatedHotelName,
  customAccommodationName,
  tripType,
  tripBuilderStops,
  hasOvernightOutbound,
  dynamicGeographyCache,
  perLegTransport,
  logger,
} = {}) {
  return {
    itinerary,
    selectedDestination,
    userProfile,
    profile,
    arrivalFlight,
    driveDetails,
    transportationMode,
    transitTimes: transitTimes || {},
    transportModes: transportModes || {},
    dynamicTransportModes: dynamicTransportModes || {},
    destinationAirports: destinationAirports || [],
    effectiveReturnMode,
    departureFlight,
    quickTransportResult,
    airportDistances: airportDistances || {},
    calculatedDriveTime,
    customTransportDesc,
    transitTimesRef: transitTimesRef || { current: {} },
    validatedFlightDurations: validatedFlightDurations || { current: {} },
    selectedHotel,
    accommodationType,
    generatedHotelName,
    customAccommodationName,
    tripType,
    tripBuilderStops: tripBuilderStops || [],
    hasOvernightOutbound: !!hasOvernightOutbound,
    dynamicGeographyCache: dynamicGeographyCache || {},
    perLegTransport: perLegTransport || {},
    logger: logger || {
      log: () => {},
      warn: (...args) => console.warn('[timeline]', ...args),
      error: (...args) => console.error('[timeline]', ...args),
    },
  };
}

export default buildWebTimelineContext;
